import { TicketData } from "../../models/event";
import {
  Button,
  Col,
  Dropdown,
  Flex,
  message,
  Row,
  Tooltip,
  MenuProps,
} from "antd";
import { dateTimeDisplayFormat } from "../../utils/DateAndTimeConvertor";
import styles from "./ticket.module.scss";
import { IconPencil, IconCopy } from "@tabler/icons-react";
import React from "react";
import { useNavigate } from "react-router";
import { ROUTES } from "../../constants/routes.constant";

import DeleteTicket from "./DeleteTicket";
import getCurrencyWithSymbol from "../../utils/getCurrencySymbol";
import { getTicketSoldQuantity } from "../../utils/ticket";

const TicketTable = ({
  handlePasteClick,
  data,
  setEditEventID,
  event_id,
  eventIndex = 0,
  readOnly = false,
}: {
  handlePasteClick: () => void;
  event_id?: string;
  data: TicketData[] | undefined | null;
  setEditEventID: React.Dispatch<React.SetStateAction<string>>;
  eventIndex: number;
  readOnly?: boolean;
}) => {
  const navigate = useNavigate();

  const items: MenuProps["items"] = [
    {
      label: (
        <Button
          type="link"
          className="w-full !border-none !shadow-none !bg-none"
          onClick={handlePasteClick}
        >
          Paste
        </Button>
      ),
      key: "1",
    },
  ];

  const colors = [
    { bg: "#79C9FF", text: "#051C5B" },
    { bg: "#FFB87A", text: "#5B2D05" },
    { bg: "#6AEAD7", text: "#0A486F" },
    { bg: "#FF8E99", text: "#92002D" },
    { bg: "#6AEA99", text: "#055B4A" },
    { bg: "#FF97D4", text: "#5B0559" },
    { bg: "#E6EA6A", text: "#6F520A" },
  ];
  const formatDistance = (distance?: string | null) => {
    if (!distance) return "N/A";

    const value = distance.toString().trim();
    const lowerValue = value.toLowerCase();
    if (lowerValue.endsWith("km") || lowerValue.endsWith("mtr")) {
      return value;
    }
    if (value.toLowerCase().endsWith("k")) {
      return `${value.slice(0, -1)} KM`;
    }

    if (value.toLowerCase().endsWith("m")) {
      return `${value.slice(0, -1)} MTR`;
    }

    return value;
  };
  return (
    <Row gutter={[24, 24]}>
      {data && data.length > 0
        ? data.map((ticket) => {
            const soldQuantity = getTicketSoldQuantity(ticket);
            const canDeleteTicket = soldQuantity <= 0;

            return (
              <Col xs={24} md={12} key={ticket.id}>
                <div className={`${styles.ticket} flex flex-col  `}>
                  <h4 className="text-[20px] ff-title text-black font-bold">
                    {ticket.name}
                  </h4>
                  <Row gutter={[24, 24]}>
                    <Col span={12}>
                      <div className="text-gray text-[12px] mt-[16px]">
                        Sale start
                      </div>
                      <div className="text-black">
                        {dateTimeDisplayFormat(
                          ticket?.registration_start_date,
                          ticket.registration_start_time,
                        )}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="text-gray text-[12px] mt-[16px]">
                        Sale end
                      </div>
                      <div className="text-black">
                        {dateTimeDisplayFormat(
                          ticket?.registration_end_date,
                          ticket.registration_end_time,
                        )}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="text-gray text-[12px] mt-[16px]">
                        Total Quantity
                      </div>
                      <div className="text-black">{ticket.quantity}</div>
                    </Col>
                    <Col span={12}>
                      <div className="text-gray text-[12px] mt-[16px]">
                        Activity
                      </div>
                      <div className="text-black capitalize">
                        {ticket.activity_type || "N/A"}
                        {ticket.distance
                          ? ` - ${formatDistance(ticket?.distance)}`
                          : ""}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="text-gray text-[12px] mt-[16px]">
                        Event Type
                      </div>
                      <div className="text-black capitalize">
                        {ticket.event_type?.replace("_", " ") || "N/A"}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="text-gray text-[12px] mt-[16px]">
                        Age Limit
                      </div>
                      <div className="text-black">
                        {ticket.min_age ?? 0} - {ticket.max_age ?? "No limit"}{" "}
                        yrs
                      </div>
                    </Col>
                    {/* <Col span={12}>
                      <div className="text-gray text-[12px] mt-[16px]">
                        Per Transaction
                      </div>
                      <div className="text-black">
                        {ticket.min_per_transaction ?? 1} -{" "}
                        {ticket.max_per_transaction ?? "No limit"} tickets
                      </div>
                    </Col> */}
                  </Row>
                  <div className={styles.zigzagPattern} />
                  <div
                    style={{
                      backgroundColor: colors[eventIndex % colors.length].bg,
                      color: colors[eventIndex % colors.length].text,
                    }}
                    className={`${styles.ticketFooter} flex items-center justify-between`}
                  >
                    {getCurrencyWithSymbol(
                      ticket.currency,
                      parseFloat(ticket.price.toString()),
                    )}

                    <Flex>
                      <Tooltip title="Copy ticket">
                        <Button
                          type="text"
                          shape="circle"
                          disabled={readOnly}
                          onClick={(e) => {
                            e.stopPropagation();
                            const copiedData = JSON.stringify({
                              ...ticket,
                              id: "",
                            });

                            navigator.clipboard.writeText(copiedData);
                            message.success("successfully copied");
                          }}
                          icon={<IconCopy size={18} />}
                        />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button
                          type="text"
                          shape="circle"
                          disabled={readOnly}
                          onClick={() => {
                            navigate(
                              ROUTES.TICKET_FORM.path
                                .replace(":id", event_id!)
                                .replace(":ticketId?", ticket.id!),
                            );
                          }}
                          icon={<IconPencil size={18} />}
                        />
                      </Tooltip>
                      {!readOnly && canDeleteTicket && (
                        <DeleteTicket eventID={event_id!} id={ticket.id!} />
                      )}
                    </Flex>
                  </div>
                </div>
              </Col>
            );
          })
        : null}
      <Col xs={24} lg={12}>
        {readOnly ? (
          <div
            className={`${styles.addTicket} border !border-dashed p-[30px!important] border-gray-300 flex justify-center text-center !items-center text-gray-300`}
            style={{ cursor: "not-allowed", opacity: 0.5 }}
          >
            Add ticket (disabled during review)
          </div>
        ) : (
          <Dropdown
            menu={{ items }}
            trigger={["contextMenu"]}
            onOpenChange={() => {
              setEditEventID(event_id!);
            }}
          >
            <div
              className={`${styles.addTicket} border !border-dashed p-[30px!important] border-gray-400 flex justify-center text-center !items-center text-gray-400`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(
                  ROUTES.TICKET_FORM.path
                    .replace(":id", event_id!)
                    .replace(":ticketId?", ""),
                );
              }}
            >
              {!data || data.length === 0
                ? "Create your first ticket"
                : "Left click to add ticket or right click to paste ticket"}
            </div>
          </Dropdown>
        )}
      </Col>
    </Row>
  );
};

export default TicketTable;
